import { ActionButtons, SelectField } from '@dispatch/web/ui';
import { Grid } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DroppableProvided,
  DraggableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { TaskFormComponentProps } from '@dispatch/shared/types';
import React from 'react';

interface OrderPriority {
  id: string;
  order: number;
}

export const ResourcePriority = ({
  name,
  control,
  errors,
  setValue,
  getValues,
  options,
}: TaskFormComponentProps) => {
  const { watch, reset } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const rolePriorities = getValues && getValues('rolePriorities');
    const reorderedItems = reorder(
      result.source.index,
      result.destination.index,
      rolePriorities
    );

    const items = reorderedItems?.map((item: OrderPriority, index: number) => {
      item.order = index;
      return item;
    });

    setValue && setValue('rolePriorities', items);
  };

  const reorder = (
    startIndex: number,
    endIndex: number,
    list?: OrderPriority[]
  ) => {
    if (!list) return;
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  React.useEffect(() => {
    // add to show at least one empty field
    if (fields.length === 0) {
      append({ id: '', order: 0 });
      const currentValues = watch();
      reset(currentValues, { keepDirty: false });
    }
  }, [fields]);

  return (
    <div style={{ width: '100%' }} data-testid="resource-priorities">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(dropProvided: DroppableProvided) => (
            <ul
              {...dropProvided.droppableProps}
              ref={dropProvided.innerRef}
              style={{
                listStyle: 'none',
                padding: '0',
                paddingLeft: '-12px !important',
              }}
            >
              {fields.map((field, index) => (
                <Draggable key={index} draggableId={`${index}`} index={index}>
                  {(provided: DraggableProvided) => (
                    <li
                      style={{ margin: '1.5625rem 0' }}
                      key={field.id}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{ padding: '0 !important' }}
                      >
                        <Grid
                          item
                          xs={11}
                          sx={{ padding: '0.625rem 0 !important' }}
                        >
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            marginLeft={0}
                          >
                            <Grid item xs={1}>
                              <div
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'end',
                                }}
                              >
                                <p style={{ padding: '0 0.3125rem' }}>{`${
                                  index + 1
                                }.`}</p>
                              </div>
                            </Grid>
                            <Grid item xs={10}>
                              <SelectField
                                key={index}
                                id={`${name}[${index}].id`}
                                name={`${name}[${index}].id`}
                                label="Resource"
                                control={control}
                                errors={errors}
                                error={
                                  errors[name] && errors[name][index]?.id
                                    ? errors[name][index]?.id?.message
                                    : null
                                }
                                options={options || []}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={1}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              marginLeft: '-26px',
                            }}
                          >
                            <div {...provided.dragHandleProps}>
                              <DragHandleIcon />
                            </div>
                            <ActionButtons
                              index={index}
                              fields={fields}
                              append={() =>
                                append({ id: '', order: fields.length })
                              }
                              remove={() => remove(index)}
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </li>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
